<script setup lang="ts">
import { LogIn, Pencil } from 'lucide';
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue';
import EventBus from '@legacy/js/service/EventBus';
import type { Notify } from '@/composables/notifications';
import { SphButton } from '@/components/base/button';
import { SphField } from '@/components/base/field';
import { SphInput } from '@/components/base/input';
import { SphSelect } from '@/components/base/select';
import TableSortHeader from './TableSortHeader.vue';
import axios from 'axios';
import { useI18n } from '@/composables/i18n';
import { useTableSort } from '@/composables/tableSort';

defineOptions({
    compatConfig: { MODE: 3 },
})

const props = defineProps<{
    federationId: number
    loginAllowed: boolean
    showSectionFilter?: boolean
    provinces?: Record<string, string>
    loginBaseUrl: string
    editBaseUrl: string
}>()

const { i18n } = useI18n()
const notify = inject<Notify>('notify')!

interface Section { id: number; name: string }
interface Club {
    id: number; name: string; street: string; zip: string; city: string; province: string | null
    sections: Section[]
}

const clubs = ref<Club[]>([])
const sections = ref<Section[]>([])
const loading = ref(true)
const filterSearch = ref('')
const filterSectionId = ref<string | undefined>(undefined)
const filterProvince = ref<string | undefined>(undefined)
const filterCity = ref<string | undefined>(undefined)

const sectionOptions = computed(() =>
    sections.value.map(s => ({ value: String(s.id), label: s.name }))
)

const provinceOptions = computed(() => {
    const seen = new Set<string>()
    return clubs.value
        .filter(c => c.province && props.provinces?.[c.province])
        .filter(c => { if (seen.has(c.province!)) return false; seen.add(c.province!); return true })
        .map(c => ({ value: c.province!, label: props.provinces![c.province!] }))
        .sort((a, b) => a.label.localeCompare(b.label))
})

const cityOptions = computed(() => {
    const source = filterProvince.value
        ? clubs.value.filter(c => c.province === filterProvince.value)
        : clubs.value
    const seen = new Set<string>()
    return source
        .filter(c => c.city)
        .filter(c => { if (seen.has(c.city)) return false; seen.add(c.city); return true })
        .map(c => ({ value: c.city, label: c.city }))
        .sort((a, b) => a.label.localeCompare(b.label))
})

const filtered = computed(() =>
    clubs.value.filter(c => {
        if (filterSearch.value && !c.name.toLowerCase().includes(filterSearch.value.toLowerCase())) return false
        if (filterSectionId.value && !c.sections.some(s => String(s.id) === filterSectionId.value)) return false
        if (filterProvince.value && c.province !== filterProvince.value) return false
        if (filterCity.value && c.city !== filterCity.value) return false
        return true
    })
)

const { sortKey, sortDirection, toggleSort, sorted } = useTableSort(filtered, {
    name: c => c.name,
    address: c => c.city,
    province: c => (props.provinces ?? {})[c.province ?? ''] ?? '',
}, { storageKey: 'federation:clubList:sort' })

watch(filterProvince, () => { filterCity.value = undefined })

const loadSections = () => {
    axios.get(`/federations/${props.federationId}/sections`)
        .then(r => { sections.value = r.data.data.map((d: any) => ({ id: d.id, name: d.name })) })
        .catch(() => notify.error('error.loadingData'))
}

let unsubscribeSection: (() => void) | undefined

onMounted(() => {
    axios.get(`/federations/${props.federationId}/clubs`)
        .then(r => {
            clubs.value = r.data.data.map((d: any) => ({
                id: d.id, name: d.name, street: d.street ?? '',
                zip: d.zip ?? '', city: d.city ?? '', province: d.province ?? null,
                sections: (d.sections ?? []).map((s: any) => ({ id: s.id, name: s.name })),
            }))
        })
        .catch(() => notify.error('error.loadingData'))
        .finally(() => { loading.value = false })
    if (props.showSectionFilter !== false) loadSections()
    unsubscribeSection = EventBus.$on('section:changed', (federationId: number) => {
        if (federationId === props.federationId) loadSections()
    })
})

onUnmounted(() => { unsubscribeSection?.() })

defineExpose({ clubs, sections, filtered, sorted, sortKey, sortDirection, toggleSort, provinceOptions, cityOptions, filterSearch, filterSectionId, filterProvince, filterCity })

const printPage = () => window.print()
</script>

<template>
    <div>
        <div class="kgr-row kgr-postfix kgr-postfix--between items-end">
            <div class="flex gap-4">
                <SphField
                    v-if="showSectionFilter !== false"
                    :label="i18n.t('Bereich')"
                >
                    <SphSelect
                        v-model="filterSectionId"
                        :options="sectionOptions"
                        :placeholder="i18n.t('Alle Bereiche')"
                        clearable
                    />
                </SphField>
                <SphField :label="i18n.t('Bundesland')">
                    <SphSelect
                        v-model="filterProvince"
                        :options="provinceOptions"
                        :placeholder="i18n.t('Alle Bundesländer')"
                        clearable
                    />
                </SphField>
                <SphField :label="i18n.t('Ort')">
                    <SphSelect
                        v-model="filterCity"
                        :options="cityOptions"
                        :placeholder="i18n.t('Alle Orte')"
                        clearable
                    />
                </SphField>
            </div>
            <SphInput
                v-model="filterSearch"
                :placeholder="i18n.t('Suche in dieser Liste ...')"
            />
        </div>

        <div
            v-if="loading"
            class="kgr-loading"
        ></div>

        <div
            v-else-if="filtered.length === 0"
            class="kgr-alert kgr-alert--info"
        >
            {{ i18n.t('Keine Vereine eingetragen') }}
        </div>

        <div v-else>
            <div class="kgr-row">
                <table class="kgr-table kgr-table--striped kgr-table--block table-groups">
                    <thead>
                        <tr class="kgr-table__row">
                            <TableSortHeader
                                :label="i18n.t('Name des Vereins')"
                                sort-key="name"
                                :active-key="sortKey"
                                :direction="sortDirection"
                                @sort="toggleSort"
                            />
                            <TableSortHeader
                                :label="i18n.t('Adresse')"
                                sort-key="address"
                                :active-key="sortKey"
                                :direction="sortDirection"
                                @sort="toggleSort"
                            />
                            <TableSortHeader
                                :label="i18n.t('Bundesland')"
                                sort-key="province"
                                :active-key="sortKey"
                                :direction="sortDirection"
                                @sort="toggleSort"
                            />
                            <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions">
                                {{ i18n.t('options') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="club in sorted"
                            :key="club.id"
                            class="kgr-table__row"
                        >
                            <td class="kgr-table__cell">
                                <a
                                    v-if="loginAllowed"
                                    :href="`${loginBaseUrl}/${club.id}/dashboard`"
                                    class="kgr-link kgr-link--unobtrusive"
                                >{{ club.name }}</a>
                                <template v-else>
                                    {{ club.name }}
                                </template>
                            </td>
                            <td class="kgr-table__cell">
                                <div class="kgr-text kgr-text--truncated kgr-text--noWrap">
                                    {{ club.street }}
                                </div>
                                <div class="kgr-text kgr-text--truncated kgr-text--noWrap">
                                    {{ club.zip }} {{ club.city }}
                                </div>
                            </td>
                            <td class="kgr-table__cell">
                                {{ (provinces ?? {})[club.province ?? ''] || '-' }}
                            </td>
                            <td class="kgr-table__cell kgr-table__cell--actions">
                                <template v-if="loginAllowed">
                                    <SphButton
                                        :icon="Pencil"
                                        :as="'a'"
                                        :href="`${editBaseUrl}/${club.id}/edit`"
                                        :title="i18n.t('edit')"
                                        variant="ghost"
                                        size="sm"
                                        class="kgr-table__action"
                                    />
                                    <SphButton
                                        :icon="LogIn"
                                        :as="'a'"
                                        :href="`${loginBaseUrl}/${club.id}/dashboard`"
                                        :title="i18n.t('login')"
                                        variant="ghost"
                                        size="sm"
                                        class="kgr-table__action"
                                    />
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="kgr-postfix kgr-postfix--between">
                <div class="kgr-postfix__fixed"></div>
                <div class="kgr-postfix__fixed">
                    <SphButton
                        :label="i18n.t('print')"
                        variant="primary"
                        class="kgr-hidden-print"
                        @click="printPage"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
